import {
	EFxIdSdkAdapterInfoSocialCapability,
	FxIdSdkAdapterSocialSettingDefault,
	FxIdSdkAdapterStickyBannerShowErrorReason,
	FxIdSdkBaseAdapter,
	IExportedFxIdSdkMethod,
	IFxIdSdkAdapterBuyProductRequest,
	IFxIdSdkAdapterBuyProductResponse,
	IFxIdSdkAdapterCanReviewResult,
	IFxIdSdkAdapterInfoSocial,
	IFxIdSdkAdapterRequestReviewResult,
	IFxIdSdkAdapterSocialSettings,
	IFxIdSdkAdapterStatEventRequest,
	IFxIdSdkAdapterStatInitializeRequest,
	IFxIdSdkAdapterStickyBannerShowResultDto
} from "./FxIdSdkBaseAdapter";
import OpenApiClient from "../Api/OpenApiClient";
import {
	FxIdApplicationStoreCreatePaymentHandlerEmbeddingType,
	FxIdDomainSettingsEGameOrientation,
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxGamesLibWebPublicDataBase,
	FxIdWebFeaturesStoreCreatePaymentRequest
} from "../Api/gen";
import { YaGamesSdk } from "../yagamessdk";
import { userStore } from "../Stores";
import { getAuthToken, registerYandexStorage } from "@app/Api/Auth";
import { IframeType } from "@app/Screens/WebPlayer/Hooks";
import { FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus } from "../Api/gen";
import { Payments, Player, Signature } from "ysdk";

export class FxIdSdkAdapterForYandexGames extends FxIdSdkBaseAdapter {
	readonly adapter: IframeType = "yandex";

	private sdk?: YaGamesSdk;

	/**
	 * Флаг блокировки покупок во время диалога выбора аккаунта.
	 * Яндекс показывает диалог когда есть два сейва (гостевой и авторизованный).
	 * Пока диалог открыт — покупки запрещены, т.к. после закрытия диалога
	 * uniqueID может измениться и текущий аккаунт сменится на другой.
	 */
	private _accountSelectionDialogOpen = false;

	constructor(
		protected exportedSdk: IExportedFxIdSdkMethod,
		protected game: string,
		protected config: FxGamesLibWebPublicDataBase
	) {
		super(exportedSdk, game, config);
	}

	RegisterShareHandlers(): Promise<void> {
		return Promise.resolve();
	}

	async BuyProduct(request: IFxIdSdkAdapterBuyProductRequest): Promise<IFxIdSdkAdapterBuyProductResponse> {
		// Покупки заблокированы пока открыт диалог выбора аккаунта — после его закрытия
		// uniqueID может измениться и покупка попадёт на «чужой» аккаунт
		if (this._accountSelectionDialogOpen) {
			throw new Error("Cannot make purchases while account selection dialog is open");
		}

		const player = await this.sdk!.getPlayer({ signed: true });
		// if (player.getMode() === "lite") {
		// 	log.info("Player not authenticated. Authenticating");
		// 	await this.sdk!.auth.openAuthDialog();
		// 	log.info("Player authenticated. Reloading");
		// 	document.location.reload();
		// }

		// log.info("Player authenticated: %o", player);

		const yandexPayments = await this.sdk!.getPayments({ signed: true });

		const paymentRequest: FxIdWebFeaturesStoreCreatePaymentRequest = {
			Token: getAuthToken()!,
			Game: this.game,
			Sku: request.sku,
			WebPublishingPlatform: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.YandexGames,
			EmbeddingType: FxIdApplicationStoreCreatePaymentHandlerEmbeddingType.Embed,
			ProductDescriptionHint: request.productDescriptionHint,
			ProductNameHint: request.productNameHint
		};
		if (request.developerPayload != null) {
			paymentRequest.DeveloperPayload = {
				MerchantDeveloperPayload: request.developerPayload
			};
		}
		const createPaymentResult = await OpenApiClient.Store.fxIdWebFeaturesStoreCreatePaymentEndpoint(paymentRequest);

		log.info("Received result from server: %o", createPaymentResult);

		try {
			log.info("Requesting payment from yandex");
			const purchase = await yandexPayments.purchase({
				id: createPaymentResult.YandexGames!.StoreSpecificOrderProductSku,
				developerPayload: JSON.stringify({
					public_order_id: createPaymentResult.TransactionId
				})
			});

			await this.VerifyPurchase(purchase, yandexPayments);

			await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint(
				{
					PublicOrderId: createPaymentResult.TransactionId,
					Status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Completed
				}
			).catch((e) => log.error("[YANDEX] Failed to set optimistic status:", e));

			return {
				transactionId: createPaymentResult.TransactionId,
				stats: {
					currency: createPaymentResult.OrderProduct.Currency ?? "",
					currencyAmount: createPaymentResult.OrderProduct.Price ?? 0,
					usdAmount: createPaymentResult.OrderProduct.UsdPrice ?? 0
				}
			};
		} catch (error) {
			await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint(
				{
					PublicOrderId: createPaymentResult.TransactionId,
					Status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Cancelled
				}
			).catch((e) => log.error("[YANDEX] Failed to set optimistic status:", e));
			throw error;
		}
	}

	private async VerifyPurchase(purchase: Signature, yandexPayments: Payments<true>) {
		log.info("Verifying payment on server %o", purchase);
		const response = await OpenApiClient.YandexGames.fxIdWebFeaturesStoreYandexGamesPurchaseEndpoint(this.game, {
			YandexSignedData: purchase.signature,
			YandexGetPurchasesRequest: false
		});

		log.info("Received response from server: %o", response);

		const responsePurchase = response.TokenSuccesses[0];

		if (!responsePurchase.Success) {
			throw new Error(`Purchase not validated ${JSON.stringify(responsePurchase.PurchaseToken)}`);
		}

		log.info("Purchase %s validated", responsePurchase.PurchaseToken);
		await yandexPayments.consumePurchase(responsePurchase.PurchaseToken);
	}

	async GetSocialInfo(): Promise<IFxIdSdkAdapterInfoSocial> {
		const userInfo = await this.GetPlayer();

		return Promise.resolve({
			social: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.YandexGames,
			paymentsAvailable: true,
			userId: userInfo.getUniqueID(),
			capabilities: [
				EFxIdSdkAdapterInfoSocialCapability.OutgoingLinksForbidden,
				EFxIdSdkAdapterInfoSocialCapability.AdsAvailable,
				EFxIdSdkAdapterInfoSocialCapability.RateApplication
			],
			socialLocale: this.sdk!.environment.i18n.lang
		});
	}

	async GetPlayer(): Promise<Player> {
		const userInfo = await this.sdk!.getPlayer();
		return userInfo;
	}

	async GetPlayerSignature(): Promise<Signature> {
		const userInfo = await this.sdk!.getPlayer({ signed: true });
		return userInfo;
	}

	async Initialize(): Promise<void> {
		log.info("Initializing Yandex adapter");

		await new Promise<void>((resolve) => {
			const s = document.createElement("script");
			s.type = "text/javascript";
			// Архив в Консоль Яндекса (standalone-пак) обязан грузить SDK ОТНОСИТЕЛЬНЫМ
			// /sdk.js — иначе модерация отклоняет («Файл содержит URL-адрес внутреннего
			// хранилища сервиса»). Абсолютный s3-URL — только для интеграции через свой
			// домен (iframe), где относительный путь не резолвится в SDK Яндекса.
			s.src =
				import.meta.env.VITE_APP_BUILD_MODE === "standalone"
					? `/sdk.js`
					: `https://sdk.games.s3.yandex.net/sdk.js`;
			s.addEventListener(
				"load",
				function (e) {
					log.info("Script loaded");
					resolve();
				},
				false
			);
			const head = document.getElementsByTagName("head")[0];
			head.appendChild(s);

			log.info("Script added");
		});

		log.info("Initializing yandex sdk");
		this.sdk = await window.YaGames.init({
			signed: true,
			screen: {
				orientation: {
					value:
						this.config.GameOrientation === FxIdDomainSettingsEGameOrientation.Horizontal
							? "landscape"
							: "portrait",
					lock: true
				}
			}
		});

		// Подписка на события диалога выбора аккаунта.
		// Платформа показывает его когда у пользователя есть два сейва (гостевой и авторизованный).
		// https://yandex.ru/dev/games/doc/ru/sdk/sdk-events
		this.sdk!.on("ACCOUNT_SELECTION_DIALOG_OPENED", () => {
			this._accountSelectionDialogOpen = true;
			log.info("[YSDK] Account selection dialog opened, blocking purchases");
		});

		this.sdk!.on("ACCOUNT_SELECTION_DIALOG_CLOSED", () => {
			log.info("[YSDK] Account selection dialog closed, reloading page to re-authenticate with new uniqueID");
			document.location.reload();
		});

		try {
			log.info("Initializing Yandex SafeStorage");
			const safeStorage = await this.sdk!.getStorage();
			registerYandexStorage(safeStorage);
		} catch (err) {
			log.error("Failed to initialize Yandex SafeStorage", err);
		}

		log.info("Getting player");
		const player = await this.sdk!.getPlayer();
		userStore.getState().updateUserId(player.getUniqueID());
		userStore.getState().updateDisplayName(`#${player.getUniqueID()}`);

		log.info("Yandex sdk environment: %o", this.sdk!.environment);
	}

	override OnAfterLogin(): Promise<void> {
		this.CheckNotConsumedPurchases().then(log.info).catch(log.error);
		this.StartPromoCodes().then(log.info).catch(log.error);

		return super.OnAfterLogin();
	}

	private async CheckNotConsumedPurchases() {
		log.info("Checking purchases");
		const yandexPayments = await this.sdk!.getPayments({ signed: true });

		const notConsumedPurchases = await yandexPayments.getPurchases();

		// Хотя в доках яндекса написано что тут дескать вернутся все покупки, по факту возвращаются не законсьюмленные и постоянные
		if (notConsumedPurchases.signature == null || notConsumedPurchases.signature.length === 0) {
			log.info("All purchases consumed");
			return;
		}

		log.info("Found not consumed purchases %o", notConsumedPurchases);

		log.info("Verifying payment on server %o", notConsumedPurchases);
		const response = await OpenApiClient.YandexGames.fxIdWebFeaturesStoreYandexGamesPurchaseEndpoint(this.game, {
			YandexSignedData: notConsumedPurchases.signature,
			YandexGetPurchasesRequest: true
		});

		log.info("Received response from server: %o", response);

		for (const token of response.TokenSuccesses) {
			if (!token.Success) {
				log.error("Purchase %s not validated", token.PurchaseToken);
			}

			log.info("Purchase %s validated", token);
			await yandexPayments.consumePurchase(token.PurchaseToken);
		}
	}

	StoreCurrency(): Promise<string | undefined> {
		return Promise.resolve(undefined);
	}

	StatInitialize(request: IFxIdSdkAdapterStatInitializeRequest): Promise<void> {
		// TODO: Это говнокод и его надо убрать - приложения должны вызывать LoadingReady
		this.sdk!.features.LoadingAPI?.ready();
		return Promise.resolve(undefined);
	}

	StatEvent(request: IFxIdSdkAdapterStatEventRequest): Promise<void> {
		return Promise.resolve(undefined);
	}

	override async StoreAdditionalData(): Promise<unknown | undefined> {
		const payments = await this.sdk!.getPayments({ signed: true });
		const catalog = await payments.getCatalog();

		return { catalog: catalog };
	}

	async CanReview(): Promise<IFxIdSdkAdapterCanReviewResult> {
		const feedback = await this.sdk!.feedback.canReview();
		log.info("Feedback result: %o", feedback);
		return { canReview: feedback.value };
	}

	async RequestReview(): Promise<IFxIdSdkAdapterRequestReviewResult> {
		await this.sdk!.feedback.requestReview();
		return { shown: true };
	}

	override AdsIsVideoReady() {
		window.FxIdSdk!.DispatchAdsVideoAvailable();
		return Promise.resolve(true);
	}

	override AdsShowVideo() {
		let finished = false;

		//Елена Дубровина https://chat.rbwtech.net/rbw/pl/5adbw4dqqpbamx4fgg164bjtmc
		// 8 minutes ago
		// @Михаил Варамашвили  нам надо знать когда окно закроется, потому что мы включаем выключаем игровую музыку,
		// если мы ее выключим на video_did_close - по логике что Алесей выше скинул, она у нас начнет играть до того как рекламное окно уберется.
		// Поэтому нам нужно знать когда окно закрывается чтоб ее включить
		//
		// ВРЕМЕННО отключаем новое поведение для рбв. Надо добавить DispatchAdsWindowClosed
		const WARNING_SKIPPED_EVENT_SENT_ALWAYS_EVENT_ON_ALREADY_FINISHED_AD =
			import.meta.env.VITE_APP_TENANT_OVERRIDE === "RBW";

		this.sdk!.adv.showRewardedVideo({
			callbacks: {
				onOpen: () => {
					log.info("Video ad open.");
				},
				onRewarded: () => {
					window.FxIdSdk!.DispatchAdsFinished();
					finished = true;
					log.info("Rewarded!");
				},
				onClose: () => {
					if (WARNING_SKIPPED_EVENT_SENT_ALWAYS_EVENT_ON_ALREADY_FINISHED_AD) {
						window.FxIdSdk!.DispatchAdsSkipped();
					} else {
						if (!finished) {
							window.FxIdSdk!.DispatchAdsSkipped();
						}
					}
					log.info("Video ad closed.");
				},
				onError: (e) => {
					window.FxIdSdk!.DispatchAdsFailed();
					log.error("Error while open video ad:", e);
				}
			}
		});

		return Promise.resolve();
	}

	override AdsIsInterstitialReady() {
		window.FxIdSdk!.DispatchAdsInterstitialAvailable();
		return Promise.resolve(true);
	}

	override AdsShowInterstitial() {
		this.sdk!.adv.showFullscreenAdv({
			callbacks: {
				onOpen: () => {
					log.info("InterstitalAdv ad open.");
				},
				onClose: function (wasShown: boolean) {
					if (wasShown) {
						window.FxIdSdk!.DispatchAdsFinished();
						log.info("Rewarded!");
					} else {
						window.FxIdSdk!.DispatchAdsSkipped();
						log.info("InterstitalAdv ad closed.");
					}
				},
				onError: function (error: Error) {
					window.FxIdSdk!.DispatchAdsFailed();
					log.error("Error while open video ad:", error.message);
				}
			}
		});

		return Promise.resolve();
	}

	override async AdsStickyBannerShow(): Promise<IFxIdSdkAdapterStickyBannerShowResultDto> {
		const { stickyAdvIsShowing, reason } = await this.sdk!.adv.getBannerAdvStatus();
		log.info("[YSDK] getBannerAdvStatus result. stickyAdvIsShowing: %o, reason: %o", stickyAdvIsShowing, reason);
		if (stickyAdvIsShowing) {
			return { success: true };
		} else if (reason) {
			// Реклама не показывается.
			log.info(reason);
			return {
				success: false,
				errorReason:
					reason === "ADV_IS_NOT_CONNECTED"
						? FxIdSdkAdapterStickyBannerShowErrorReason.AdsNotConnected
						: FxIdSdkAdapterStickyBannerShowErrorReason.Unknown
			};
		} else {
			const showResult = await this.sdk!.adv.showBannerAdv();
			log.info("[YSDK] showBannerAdv result. result: %o", showResult);
			const status = await this.sdk!.adv.getBannerAdvStatus();
			if (status.stickyAdvIsShowing) {
				return { success: true };
			} else {
				return {
					success: false,
					errorReason:
						showResult.reason === "ADV_IS_NOT_CONNECTED"
							? FxIdSdkAdapterStickyBannerShowErrorReason.AdsNotConnected
							: FxIdSdkAdapterStickyBannerShowErrorReason.Unknown
				};
			}
		}
	}

	override async AdsStickyBannerHide(): Promise<IFxIdSdkAdapterStickyBannerShowResultDto> {
		const result = await this.sdk!.adv.hideBannerAdv();
		log.info("[YSDK] hideBannerAdv result: %o", result);

		return { success: true };
	}

	async LoadingReady(): Promise<void> {
		this.sdk!.features.LoadingAPI?.ready();
		void super.LoadingReady();
	}

	async GameStarted(): Promise<void> {
		this.sdk!.features.GameplayAPI?.start();
		void super.GameStarted();
	}

	async GameStopped(): Promise<void> {
		this.sdk!.features.GameplayAPI?.stop();
		void super.GameStopped();
	}

	private async StartPromoCodes(): Promise<void> {
		log.info("Start loading promo codes...");

		await this.ConsumeBonuses();
		// Бонусы - пока не в продакшене
		this.sdk!.on("BONUS_AWARDED" as any, async (bonus: { signature: string }) => {
			log.info("Received BONUS_AWARDED event: %o", bonus);
			// Александр Смолин, [27 Mar 2025 at 10:41:28]:
			// Да, все верно. Пока нужно работать через getPlayerBonuses в любых кейсах. Способ с данными пока не поддержан.
			await this.ConsumeBonuses();
		});
	}

	private async consumeBonus(bonus: { signature: string }) {
		try {
			// начислить игроку соответствующий предмет в данных игры, например
			const result = await OpenApiClient.YandexGames.fxIdWebFeaturesStoreYandexGamesRedeemYandexBonusEndpoint({
				GameSystemName: this.config.GameSystemName,
				YandexSecret: bonus.signature
			});

			log.info("Redeem bonus result: %o", result);

			if (result.Success) {
				for (const record of result.Data.Records) {
					if (record.SucccessData != null) {
						// консумировать бонус
						await this.sdk!.bonuses.consume(record.SucccessData.ConsumeToken);
						log.info("Bonus %s successfully consumed: %o", record);
					} else {
						log.error("Failed to consume bonus %s: %o", record);
					}
				}
			} else {
				log.error("Failed to redeem bonus: %o", result);
			}
		} catch (e) {
			log.error("Failed to consume bonus %o:", bonus, e);
		}
	}

	private async ConsumeBonuses(): Promise<void> {
		log.info("Start consuming bonuses...");
		try {
			// Request player bonuses
			const receivedBonuses = await this.sdk!.bonuses.getPlayerBonuses();
			log.info("Received bonuses: %o", receivedBonuses);

			// Check if bonuses with a signature were received before consuming
			if (receivedBonuses && receivedBonuses.signature) {
				await this.consumeBonus(receivedBonuses);
			} else {
				log.info("No pending bonuses found.");
			}
		} catch (e) {
			log.error("Failed to get player bonuses:", e);
		}
	}
}
